"use client";

import { useState } from "react";
import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";
import {
  type LightGoal,
  type LightResult,
  solveLight,
} from "@/lib/tool-kit/physics-waves/doppler";
import { SPEED_OF_LIGHT } from "@/lib/tool-kit/physics-waves/electromagnetic";
import {
  formatSig,
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  findUnit,
  METER_UNITS,
  RELATIVISTIC_SPEED_UNITS,
  toBase,
  type UnitDef,
} from "@/lib/tool-kit/physics-waves/units";

export interface DopplerFigureStrings {
  figureLabel: string;
  figureDescription: string;
}

const FIG_WIDTH = 560;
const FIG_HEIGHT = 190;
const RINGS = 7;
const STEP = 15;

/**
 * Wavefronts of a source moving at Mach `machNumber` (0–0.95), as circles emitted at
 * equal intervals: bunched ahead of the source, stretched behind it.
 */
export function WavefrontFigure({
  machNumber,
  approaching,
  strings,
}: {
  machNumber: number;
  approaching: boolean;
  strings: DopplerFigureStrings;
}) {
  const direction = approaching ? 1 : -1;
  const m = Math.min(Math.max(machNumber, 0), 0.95);
  const sourceX = FIG_WIDTH / 2 + direction * RINGS * STEP * m * 0.5;
  const centreY = FIG_HEIGHT / 2;
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-xl"
      description={strings.figureDescription}
      height={FIG_HEIGHT}
      id="de-figure"
      label={strings.figureLabel}
      width={FIG_WIDTH}
    >
      {Array.from({ length: RINGS }, (_, i) => {
        const age = i + 1;
        return (
          <circle
            cx={px(sourceX - direction * m * age * STEP)}
            cy={centreY}
            fill="none"
            key={age}
            r={age * STEP}
            stroke={CHART_COLORS[0]}
            strokeWidth={2}
          />
        );
      })}
      <circle cx={px(sourceX)} cy={centreY} fill={CHART_COLORS[3]} r={6} />
      <line
        stroke={CHART_INK.text}
        strokeWidth={2.5}
        x1={px(sourceX + direction * 10)}
        x2={px(sourceX + direction * 44)}
        y1={centreY}
        y2={centreY}
      />
      <polygon
        fill={CHART_INK.text}
        points={`${px(sourceX + direction * 52)},${centreY} ${px(sourceX + direction * 42)},${centreY - 6} ${px(sourceX + direction * 42)},${centreY + 6}`}
      />
    </ChartSvg>
  );
}

export interface LightStrings extends DopplerFigureStrings {
  unitFor: string;
  enterAll: string;
  working: string;
  results: string;
  lightGoalLabel: string;
  lightGoals: { speed: string; z: string; wavelengths: string };
  lightSpeed: string;
  lightDirection: string;
  lightReceding: string;
  lightApproaching: string;
  lightZ: string;
  lightRest: string;
  lightRestHint: string;
  lightObservedInput: string;
  lightEmittedInput: string;
  lightRedshift: string;
  lightBlueshift: string;
  lightBeta: string;
  lightSpeedKm: string;
  lightClassical: string;
  lightClassicalError: string;
  lightObservedWavelength: string;
  lightFrequencyRatio: string;
  lightStepWavelengths: string;
  lightStepZ: string;
  lightStepBeta: string;
  lightStepObserved: string;
  lightTooFast: string;
  lightInvalidZ: string;
}

interface Entry {
  value: string;
  unit: string;
}

const WAVELENGTH_UNITS: UnitDef[] = METER_UNITS.filter((u) =>
  ["nm", "µm", "mm", "m"].includes(u.symbol)
);

function readEntry(entry: Entry, units: readonly UnitDef[]): number | null {
  const parsed = parseNumberInput(entry.value);
  return parsed === null ? null : toBase(parsed, findUnit(units, entry.unit));
}

function LightResults({
  solution,
  rest,
  strings,
  fmt,
  sig,
}: {
  solution: Extract<LightResult, { ok: true }>;
  rest: number | null;
  strings: LightStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const { z, beta } = solution;
  const sign = (value: number) =>
    `${value > 0 ? "+" : ""}${formatSig(fmt, value, sig)}`;
  const classicalError =
    z === 0 ? "0%" : `${formatSig(fmt, ((beta - z) / z) * 100, 3)}%`;
  return (
    <StatGrid>
      <StatCard emphasis label={strings.lightZ} value={sign(z)} />
      <StatCard
        label={z < 0 ? strings.lightBlueshift : strings.lightRedshift}
        value={`${formatSig(fmt, Math.abs(z) * 100, sig)}%`}
      />
      <StatCard label={strings.lightBeta} value={sign(beta)} />
      <StatCard
        label={strings.lightSpeedKm}
        value={`${sign((beta * SPEED_OF_LIGHT) / 1000)} km/s`}
      />
      <StatCard label={strings.lightClassical} value={sign(beta)} />
      <StatCard label={strings.lightClassicalError} value={classicalError} />
      <StatCard
        label={strings.lightFrequencyRatio}
        value={formatSig(fmt, 1 / (1 + z), sig)}
      />
      {rest === null ? null : (
        <StatCard
          emphasis
          label={strings.lightObservedWavelength}
          value={withSiPrefix(fmt, rest * (1 + z), "m", sig)}
        />
      )}
    </StatGrid>
  );
}

function lightLines(
  goal: LightGoal,
  ok: Extract<LightResult, { ok: true }>,
  observed: number | null,
  rest: number | null,
  strings: LightStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const z = formatSig(fmt, ok.z, sig);
  const beta = formatSig(fmt, ok.beta, sig);
  const lines = [
    t(strings.lightStepZ, { beta, z }),
    t(strings.lightStepBeta, {
      z,
      beta,
      v: `${formatSig(fmt, Math.abs(ok.beta) * SPEED_OF_LIGHT, sig)} m/s`,
    }),
  ];
  if (rest === null) {
    return lines;
  }
  if (goal === "wavelengths" && observed !== null) {
    lines.unshift(
      t(strings.lightStepWavelengths, {
        observed: show(observed, "m"),
        rest: show(rest, "m"),
        z,
      })
    );
  } else if (goal !== "wavelengths") {
    lines.push(
      t(strings.lightStepObserved, {
        rest: show(rest, "m"),
        z,
        observed: show(rest * (1 + ok.z), "m"),
      })
    );
  }
  return lines;
}

export function LightPanel({
  strings,
  fmt,
  sig,
}: {
  strings: LightStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [goal, setGoal] = useState<LightGoal>("speed");
  const [speed, setSpeed] = useState<Entry>({ value: "30000", unit: "km/s" });
  const [receding, setReceding] = useState<"receding" | "approaching">(
    "receding"
  );
  const [zText, setZText] = useState("0.1");
  const [rest, setRest] = useState<Entry>({ value: "656.3", unit: "nm" });
  const [observed, setObserved] = useState<Entry>({ value: "722", unit: "nm" });
  const restValue = readEntry(rest, WAVELENGTH_UNITS);
  const observedValue = readEntry(observed, WAVELENGTH_UNITS);
  const solution = solveLight(goal, {
    speed: readEntry(speed, RELATIVISTIC_SPEED_UNITS),
    receding: receding === "receding",
    z: parseNumberInput(zText),
    observed: observedValue,
    emitted: restValue,
  });
  const ok = solution !== "missing" && solution.ok ? solution : null;

  return (
    <>
      <PillGroup
        label={strings.lightGoalLabel}
        name="de-light-goal"
        onChange={setGoal}
        options={[
          { value: "speed", label: strings.lightGoals.speed },
          { value: "z", label: strings.lightGoals.z },
          { value: "wavelengths", label: strings.lightGoals.wavelengths },
        ]}
        value={goal}
      />
      {goal === "speed" ? (
        <PillGroup
          label={strings.lightDirection}
          name="de-light-direction"
          onChange={setReceding}
          options={[
            { value: "receding", label: strings.lightReceding },
            { value: "approaching", label: strings.lightApproaching },
          ]}
          value={receding}
        />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {goal === "speed" ? (
          <QuantityField
            id="de-light-speed"
            label={strings.lightSpeed}
            onChange={(value) => setSpeed({ ...speed, value })}
            onUnitChange={(unit) => setSpeed({ ...speed, unit })}
            unit={speed.unit}
            unitLabel={t(strings.unitFor, { name: strings.lightSpeed })}
            units={RELATIVISTIC_SPEED_UNITS}
            value={speed.value}
          />
        ) : null}
        {goal === "z" ? (
          <TextField
            id="de-light-z"
            label={strings.lightZ}
            numeric
            onChange={setZText}
            value={zText}
          />
        ) : null}
        {goal === "wavelengths" ? (
          <QuantityField
            id="de-light-observed"
            label={strings.lightObservedInput}
            onChange={(value) => setObserved({ ...observed, value })}
            onUnitChange={(unit) => setObserved({ ...observed, unit })}
            unit={observed.unit}
            unitLabel={t(strings.unitFor, { name: strings.lightObservedInput })}
            units={WAVELENGTH_UNITS}
            value={observed.value}
          />
        ) : null}
        <QuantityField
          hint={goal === "wavelengths" ? undefined : strings.lightRestHint}
          id="de-light-rest"
          label={
            goal === "wavelengths"
              ? strings.lightEmittedInput
              : strings.lightRest
          }
          onChange={(value) => setRest({ ...rest, value })}
          onUnitChange={(unit) => setRest({ ...rest, unit })}
          unit={rest.unit}
          unitLabel={t(strings.unitFor, { name: strings.lightRest })}
          units={WAVELENGTH_UNITS}
          value={rest.value}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {solution === "missing" ? <Notice>{strings.enterAll}</Notice> : null}
        {solution !== "missing" && !solution.ok ? (
          <ErrorNotice>
            {solution.error === "too-fast"
              ? strings.lightTooFast
              : strings.lightInvalidZ}
          </ErrorNotice>
        ) : null}
        {ok === null ? null : (
          <>
            <LightResults
              fmt={fmt}
              rest={goal === "wavelengths" ? null : restValue}
              sig={sig}
              solution={ok}
              strings={strings}
            />
            <WorkingSteps
              heading={strings.working}
              lines={lightLines(
                goal,
                ok,
                observedValue,
                restValue,
                strings,
                fmt,
                sig
              )}
            />
          </>
        )}
      </section>
    </>
  );
}
